// src/components/ui/input.tsx
import * as React from "react";
import { cn } from "lib/utils";

/**
 * Props
 * - size: visual height / padding
 * - variant: visual style; "default" matches your .input-field rules
 * - leading / trailing: icon or any React node at left/right
 * - loading: shows a spinner at the right (overrides trailing)
 * - passwordToggle: for type="password", renders an eye button to toggle
 * - wrapperClassName: style the outer container separately from the input
 */
export type InputProps = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  "size"
> & {
  size?: "sm" | "md" | "lg";
  variant?: "default" | "ghost" | "underline";
  leading?: React.ReactNode;
  trailing?: React.ReactNode;
  loading?: boolean;
  passwordToggle?: boolean;
  wrapperClassName?: string;
};

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      wrapperClassName,
      size = "md",
      variant = "default",
      leading,
      trailing,
      loading = false,
      passwordToggle = false,
      type = "text",
      "aria-invalid": ariaInvalid,
      ...props
    },
    ref
  ) => {
    const [reveal, setReveal] = React.useState(false);
    const isPassword = type === "password";
    const showToggle = isPassword && passwordToggle;

    const resolvedType = showToggle ? (reveal ? "text" : "password") : type;

    // Size paddings / heights
    const sizeClasses =
      size === "sm"
        ? "h-8 text-sm"
        : size === "lg"
        ? "h-11 text-base"
        : "h-9 text-[0.95rem] md:text-sm";

    // Variant styles; "default" maps to your .input-field (light/dark handled in index.css)
    const variantClasses =
      variant === "ghost"
        ? "border-transparent bg-transparent focus:ring-2 focus:ring-blue-500/50"
        : variant === "underline"
        ? "rounded-none border-0 border-b border-line-strong focus:border-blue-500 focus:ring-0"
        : // default
          "input-field";

    // When we render leading/trailing adornments we need extra padding
    const withAdornments = [
      leading ? "pl-9" : "",
      (trailing || loading || showToggle) ? "pr-10" : "",
    ].join(" ");

    // Invalid styles via aria-invalid (works with your CSS and Tailwind)
    const invalidClasses =
      ariaInvalid ? "ring-2 ring-destructive/20 dark:ring-destructive/40 border-destructive" : "";

    return (
      <div
        className={cn(
          "relative inline-flex w-full items-center",
          wrapperClassName
        )}
      >
        {/* Leading icon / node */}
        {leading ? (
          <span className="pointer-events-none absolute left-2.5 inline-flex h-5 w-5 items-center justify-center text-content-subtle dark:text-gray-500">
            {leading}
          </span>
        ) : null}

        <input
          ref={ref}
          type={resolvedType}
          data-slot="input"
          aria-invalid={ariaInvalid}
          className={cn(
            // base resets + smooth focus (kept close to your shadcn-like setup)
            "min-w-0 w-full rounded-md outline-none transition-[color,box-shadow,border] selection:bg-blue-600 selection:text-white",
            sizeClasses,
            variantClasses,
            withAdornments,
            invalidClasses,
            // small polish
            "placeholder:text-muted-foreground file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-medium",
            "disabled:cursor-not-allowed disabled:opacity-50",
            className
          )}
          {...props}
        />

        {/* Trailing icon or spinner or password toggle */}
        <span className="absolute right-2.5 inline-flex h-5 items-center justify-center">
          {loading ? (
            <Spinner className="h-5 w-5 text-content-subtle dark:text-gray-500" />
          ) : showToggle ? (
            <button
              type="button"
              aria-label={reveal ? "Hide password" : "Show password"}
              onClick={() => setReveal((s) => !s)}
              className={cn(
                "grid h-5 w-5 place-items-center text-content-subtle hover:text-content-secondary",
                "dark:text-gray-500 dark:hover:text-gray-300",
                "focus-ring"
              )}
              tabIndex={-1}
            >
              {reveal ? <EyeOffIcon /> : <EyeIcon />}
            </button>
          ) : trailing ? (
            <span className="inline-flex w-5 justify-center text-content-subtle dark:text-gray-500">
              {trailing}
            </span>
          ) : null}
        </span>
      </div>
    );
  }
);
Input.displayName = "Input";

/* ------------------------------------------------------------------ */
/* Small inline icons (no external deps)                               */
/* ------------------------------------------------------------------ */
function EyeIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true">
      <path d="M12 5c4.5 0 8.3 2.9 10 7-1.7 4.1-5.5 7-10 7S3.7 16.1 2 12c1.7-4.1 5.5-7 10-7Zm0 3a4 4 0 1 0 .001 8.001A4 4 0 0 0 12 8Z" />
    </svg>
  );
}
function EyeOffIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true">
      <path d="M3.3 2.3 21.7 20.7l-1.4 1.4-3-3A11.7 11.7 0 0 1 12 19c-4.5 0-8.3-2.9-10-7a12.9 12.9 0 0 1 5-5.9L1.9 3.7l1.4-1.4ZM12 7c4.2 0 7.7 2.4 9.5 6-1 2.1-2.7 3.9-4.8 5.1L14 15.4A4 4 0 0 0 8.6 10L7 8.4A10.9 10.9 0 0 1 12 7Z" />
    </svg>
  );
}

/** Minimal spinner that respects dark mode */
function Spinner({ className }: { className?: string }) {
  return (
    <svg
      className={cn("animate-spin", className)}
      viewBox="0 0 24 24"
      width="18"
      height="18"
      aria-hidden="true"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
        fill="none"
      />
      <path
        className="opacity-90"
        fill="currentColor"
        d="M4 12a8 8 0 0 1 8-8v4a4 4 0 0 0-4 4H4z"
      />
    </svg>
  );
}

export default Input;
